import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Edit, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { roadmapAPI, RoadmapAPIError } from '../utils/roadmapAPI';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import Toast from './Toast';

const SavedRoadmapsList = ({ onLoadRoadmap, currentRoadmapId, onRoadmapDeleted }) => {
  const [roadmaps, setRoadmaps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  
  // Estados para modal de confirmação e toast
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roadmapToDelete, setRoadmapToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Função para mostrar toast
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Função para fechar toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Função para abrir modal de confirmação
  const openDeleteModal = (roadmap) => {
    setRoadmapToDelete(roadmap);
    setShowDeleteModal(true);
  };

  // Função para fechar modal de confirmação
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRoadmapToDelete(null);
  };
  
  useEffect(() => {
    fetchRoadmaps();
  }, []);
  
  const fetchRoadmaps = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await roadmapAPI.list();
      setRoadmaps(response.data.roadmaps);
    } catch (error) {
      console.error('Erro ao carregar roadmaps:', error);
      setError(error instanceof RoadmapAPIError ? error.message : 'Erro ao carregar roadmaps salvos');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLoad = async (roadmap) => {
    try {
      const response = await roadmapAPI.load(roadmap.id);
      onLoadRoadmap(response.data.roadmap);
    } catch (error) {
      console.error('Erro ao carregar roadmap:', error);
      alert(error instanceof RoadmapAPIError ? error.message : 'Erro ao carregar roadmap');
    }
  };
  
  const handleDelete = async () => {
    if (!roadmapToDelete) return;

    try {
      setDeletingId(roadmapToDelete.id);
      
      const response = await roadmapAPI.delete(roadmapToDelete.id);
      
      fetchRoadmaps(); // Recarregar lista
      
      // Mostrar toast de sucesso
      showToast(
        response.message || `Roadmap "${roadmapToDelete.name}" excluído com sucesso!`,
        'success'
      );
      
      // Se o roadmap excluído era o atual, notificar
      if (currentRoadmapId === roadmapToDelete.id && onRoadmapDeleted) {
        onRoadmapDeleted();
      }
      
      // Fechar modal
      closeDeleteModal();
      
    } catch (error) {
      console.error('Erro ao excluir roadmap:', error);
      
      // Mostrar toast de erro
      showToast(
        error instanceof RoadmapAPIError ? error.message : 'Erro ao excluir roadmap',
        'error'
      );
    } finally {
      setDeletingId(null);
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
        <p>Carregando roadmaps salvos...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
        <p className="font-medium mb-2">Erro ao carregar</p>
        <p className="text-sm mb-3">{error}</p>
        <button
          onClick={fetchRoadmaps}
          className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  
  if (roadmaps.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium mb-1">Nenhum roadmap salvo</p>
        <p className="text-sm">Crie seu primeiro roadmap e clique em "Salvar"</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">
          Roadmaps Salvos ({roadmaps.length})
        </h3>
        <button
          onClick={fetchRoadmaps}
          className="text-xs text-gray-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-gray-50"
          title="Atualizar lista"
        >
          Atualizar
        </button>
      </div>
      
      {roadmaps.map((roadmap) => {
        const isCurrentRoadmap = currentRoadmapId === roadmap.id;
        
        return (
          <div 
            key={roadmap.id}
            className={`border rounded-lg p-4 transition-colors ${
              isCurrentRoadmap 
                ? 'border-blue-300 bg-blue-50' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium mb-1 truncate ${
                  isCurrentRoadmap ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {roadmap.name}
                  {isCurrentRoadmap && (
                    <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                      Atual
                    </span>
                  )}
                </h4>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(roadmap.updated_at)}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-1 ml-3">
                <button
                  onClick={() => handleLoad(roadmap)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title="Editar roadmap"
                  aria-label="Editar roadmap"
                  disabled={isCurrentRoadmap}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openDeleteModal(roadmap)}
                  disabled={deletingId === roadmap.id}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                  title="Excluir roadmap"
                  aria-label="Excluir roadmap"
                >
                  {deletingId === roadmap.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Modal de confirmação de exclusão */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        roadmapName={roadmapToDelete?.name || ''}
        isDeleting={deletingId === roadmapToDelete?.id}
      />
      
      {/* Toast de notificação */}
      <Toast
        show={toast.show}
        onClose={closeToast}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default SavedRoadmapsList;
